'use client'
import { memo, useState, useCallback, useEffect, useRef } from "react";

interface ImageComponentProps {
    src: string;
    className?: string;
    style?: React.CSSProperties;
	id?: string
    /**
     * 1-based page to show when `src` is a multi-page PDF. Clamped to the document's
     * page count, so asking for page 2 of a single-page file falls back to page 1.
     * Ignored for every other content type.
     */
    page?: number
}

// How the fetched resource should be rendered. `remote` means we could not read the
// bytes (CORS, network) and have to fall back to letting the browser load the URL itself.
type ContentKind = 'svg' | 'image' | 'pdf' | 'binary' | 'remote'

type LoadedContent = {
    kind: ContentKind
    svgContent: string | null
    // Blob URL for the fetched bytes; needs revoking when it is replaced.
    objectUrl: string | null
    // What the <img> branch renders — a blob URL for real images, a data URL for a
    // rasterised PDF page.
    imageUrl: string | null
}

const isSvg = (contentType: string | null, url: string) => (
    (contentType != null && (
        contentType.includes('image/svg+xml') ||
        contentType.startsWith('text/xml') ||
        contentType.startsWith('text/plain')
    )) ||
    url.split('?')[0].endsWith('.svg')
)

const isPdf = (contentType: string | null, url: string) => (
    (contentType != null && contentType.includes('application/pdf')) ||
    url.split('?')[0].toLowerCase().endsWith('.pdf')
)

// Width of the rasterised PDF page. Generous enough that the fullscreen preview stays
// sharp, while the thumbnail just scales it down.
const PDF_RENDER_WIDTH = 1200

// Rasterise one page of a PDF so it can be shown as an ordinary image. pdf.js is
// imported lazily so it only reaches browsers that actually open a PDF, and the worker
// URL is resolved by Vite rather than fetched from a CDN.
const renderPdfPage = async (data: ArrayBuffer, page: number): Promise<string | null> => {
    try {
        const [pdfjs, worker] = await Promise.all([
            import('pdfjs-dist'),
            import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ])
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default

        const loadingTask = pdfjs.getDocument({data})
        const document = await loadingTask.promise
        const pageNumber = Math.min(Math.max(Math.trunc(page) || 1, 1), document.numPages)
        const pdfPage = await document.getPage(pageNumber)
        const baseViewport = pdfPage.getViewport({scale: 1})
        const viewport = pdfPage.getViewport({scale: PDF_RENDER_WIDTH / baseViewport.width})

        const canvas = window.document.createElement('canvas')
        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)
        const context = canvas.getContext('2d')
        if (!context) return null

        // Raw designs are usually black line art on a transparent page; without this they
        // render as black-on-black once the checkerboard backdrop shows through.
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, canvas.width, canvas.height)

        await pdfPage.render({canvas, canvasContext: context, viewport}).promise
        await loadingTask.destroy()

        return canvas.toDataURL('image/png')
    } catch (error) {
        console.error('Error rendering PDF preview:', error)
        return null
    }
}

const prepareSvg = (svgText: string) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
    const svgElement = svgDoc.querySelector('svg');
    if (svgElement) {
        const originalWidth = Number.parseFloat(svgElement.getAttribute('width') || '');
        const originalHeight = Number.parseFloat(svgElement.getAttribute('height') || '');

        if (
            !svgElement.hasAttribute('viewBox') &&
            Number.isFinite(originalWidth) &&
            Number.isFinite(originalHeight) &&
            originalWidth > 0 &&
            originalHeight > 0
        ) {
            svgElement.setAttribute('viewBox', `0 0 ${originalWidth} ${originalHeight}`);
        }

        svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svgElement.style.width = 'auto';
        svgElement.style.height = 'auto';
        svgElement.style.maxWidth = '100%';
        svgElement.style.maxHeight = '100%';
        svgElement.style.display = 'block';
    }

    return new XMLSerializer().serializeToString(svgDoc);
};

// Fetch the resource once and hand back something renderable. The body is kept as an
// object URL rather than re-requesting `url` from the <img>/<embed>: that halves the
// requests, and — crucially — strips any `Content-Disposition: attachment` header. A
// remote URL carrying that header makes the browser download the file instead of
// displaying it, which is why every raw PDF on an order page used to hit the disk.
const fetchContent = async (url: string, page: number): Promise<LoadedContent> => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        const contentType = response.headers.get("Content-Type");

        if (isSvg(contentType, url)) {
            return {kind: 'svg', svgContent: prepareSvg(await response.text()), objectUrl: null, imageUrl: null};
        }

        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], {type: response.headers.get('Content-Type') || ''});
        // Hosts often serve application/octet-stream, so fall back to the blob's own
        // sniffed type and then to the extension before deciding how to render.
        const resolvedType = contentType && !contentType.startsWith('application/octet-stream')
            ? contentType
            : blob.type || contentType

        if (isPdf(resolvedType, url)) {
            const pageImage = await renderPdfPage(buffer, page);
            // Fall back to the browser's own PDF viewer if rasterising failed — still
            // pointed at a blob URL, so it displays rather than downloads.
            return pageImage
                ? {kind: 'image', svgContent: null, objectUrl: null, imageUrl: pageImage}
                : {kind: 'pdf', svgContent: null, objectUrl: URL.createObjectURL(blob), imageUrl: null};
        }

        const objectUrl = URL.createObjectURL(blob);
        const kind: ContentKind = resolvedType?.startsWith('image/') ? 'image' : 'binary'

        return {kind, svgContent: null, objectUrl, imageUrl: kind === 'image' ? objectUrl : null};
    } catch (error) {
        console.error('Error fetching or processing image:', error);
        // Reading the bytes failed (usually CORS). A plain <img> still renders
        // cross-origin images without CORS, so try that rather than <embed> — an
        // <embed> pointed at a remote URL is what triggers the download behaviour.
        return {kind: 'remote', svgContent: null, objectUrl: null, imageUrl: null};
    }
};

const ImageComponent: React.FC<ImageComponentProps> = memo(({ src, className = "", style={}, id, page = 1 }) => {
    const [loaded, setLoaded] = useState(false);
    const [content, setContent] = useState<LoadedContent>({kind: 'remote', svgContent: null, objectUrl: null, imageUrl: null});
    const objectUrlRef = useRef<string | null>(null);

    const revokeObjectUrl = useCallback(() => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        setLoaded(false);
        revokeObjectUrl();

        if (!src) {
            setContent({kind: 'remote', svgContent: null, objectUrl: null, imageUrl: null});
            setLoaded(true);
            return;
        }

        fetchContent(src, page).then((next) => {
            if (cancelled) {
                if (next.objectUrl) URL.revokeObjectURL(next.objectUrl);
                return;
            }
            objectUrlRef.current = next.objectUrl;
            setContent(next);
            setLoaded(true);
        });

        return () => {
            cancelled = true;
            revokeObjectUrl();
        };
    }, [src, page, revokeObjectUrl]);

    const sharedStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        opacity: loaded ? 1 : 0,
        transition: 'opacity 0.3s',
        ...style
    };

    const renderContent = () => {
        if (content.kind === 'svg' && content.svgContent) {
            return (
                <div
                    dangerouslySetInnerHTML={{ __html: content.svgContent }}
                    style={{
                        ...sharedStyle,
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    id={id}
                />
            );
        }

        if (content.kind === 'pdf' && content.objectUrl) {
            return (
                <embed
                    src={`${content.objectUrl}#page=${page}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    id={id}
                    style={{...sharedStyle, objectFit: 'contain'}}
                />
            );
        }

        if (content.kind === 'binary' && content.objectUrl) {
            return (
                <embed
                    src={content.objectUrl}
                    width="100%"
                    height="100%"
                    id={id}
                    style={{...sharedStyle, objectFit: 'contain'}}
                />
            );
        }

        // Bytes were unreadable (the prod-3dplugin bucket serves no CORS headers, so every
        // fetch against it fails). A PDF cannot go in an <img>, so hand it to the browser's
        // viewer. This is the one remaining path that can download instead of display, and
        // only for a URL that is both CORS-blocked and sent as an attachment.
        if (content.kind === 'remote' && isPdf(null, src)) {
            return (
                <embed
                    src={`${src}#page=${page}&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    id={id}
                    style={{...sharedStyle, objectFit: 'contain'}}
                />
            );
        }

        return (
            <img
                src={content.imageUrl || src}
                alt="Content"
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                style={{...sharedStyle, objectFit: 'contain'}}
                id={id}
            />
        );
    };

    return (
        <div
            style={{
                position: 'relative',
                height: '140px',
                width: '140px',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                ...style
            }}
            className={className}
        >
            <img
                src={'/assets/images/Loading.gif'}
                alt="Loading"
                style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: loaded ? 0 : 1,
                    transition: 'opacity 0.3s',
                    ...style
                }}
            />
            {renderContent()}
        </div>
    );
});

ImageComponent.displayName = "ImageComponent";

export default memo(
    ImageComponent,
    (prevProps, nextProps) => prevProps.src === nextProps.src && (prevProps.page ?? 1) === (nextProps.page ?? 1)
);
